import i18n from "i18next";
import { GetPaymentOptions } from "api/rpc/2024-04/facilityAdmin/facility/paymentOption";
import { GetDiscount } from "api/rpc/2022-09/facilityAdmin/product/discount";
import { StatusCode } from "api/protocols";
import { GetWeather } from "api/rpc/weather/weather";
import {
  GetFacilityPrinterSettings,
  IPutPrinterSettings,
  IPutTeeSheetSettings,
  PutFacilityPrinterSettings,
  GetFacilityTeeSheetSettings,
  PutFacilityTeeSheetSettings,
} from "api/rpc/2024-04/facilityAdmin/facility/settings";
import { GetKitchenMeals } from "api/rpc/2022-09/facilityAdmin/facility/kitchen";
import { IKitchenCourse } from "pages/secure/facility/settings/kitchenCourse/KitchenCourse";
import { GetFacility, TGetFacility } from "api/rpc/2024-04/facilityAdmin/facility/facility";
import { GetProductRequirements } from "api/rpc/2024-04/facilityAdmin/client/requirements";
import { CancelToken } from "axios";
import { TProductInputsRequired, TProductRequirements } from "redux/reducers/models/product";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { GetLoyaltyProgram, GetLoyaltyProgramOffers } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import { ILoyaltyProgram, IOffer } from "redux/reducers/models/loyalty";
import { showError, showSuccess } from "./ui";
import { errorMessage, getOpenWeatherLang } from "helpers/Helpers";
import { GetServiceFees, IGetServiceFees } from "api/rpc/2024-04/facilityAdmin/payment/serviceFee";
import { FACILITY_DISPATCH_TARGET_FACILITY } from "redux/facilityDispatchMeta";

const { t } = i18n;

export function loadAdminFacility(params: TGetFacility, useGlobalLoader: boolean) {
  return async (dispatch: any) => {
    const res = await GetFacility(params, useGlobalLoader);

    if (res.status !== StatusCode.OK) {
      console.log(res);
      return;
    }

    console.log("Admin Facility: ", res.data[0]);

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        isLoaded: true,
        facility: res.data[0],
      },
    });
  };
}

export function loadPaymentOptions(token?: CancelToken, reload?: boolean) {
  return async (dispatch: any, getState: any) => {
    if (getState().facilityStore.paymentOptions.length === 0 || reload) {
      try {
        const paymentRes = await GetPaymentOptions({ active: true }, token ? false : true, token);
        if (token && token.reason) {
          return;
        }
        console.log("paymentRes: ", paymentRes);
        if (paymentRes.status === StatusCode.OK) {
          // push options to redux

          const paymentOptions = paymentRes.data;

          dispatch({
            type: "facility.update",
            meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
            payload: {
              isLoaded: true,
              paymentOptions: paymentOptions,
            },
          });
        }
      } catch (e) {
        console.log("GetPaymentOptions ERR:", e);
      }
    }
  };
}

export function loadRegisters(token?: CancelToken, reload?: boolean) {
  return async (dispatch: any, getState: any) => {
    if (getState().facilityStore.registers.length === 0 || reload) {
      try {
        const registersRes = await GetRegisters({ extended: true, active_shift: true }, false, token);

        if (token && token.reason) {
          return;
        }

        console.log("registersRes: ", registersRes);
        if (registersRes.status === StatusCode.OK) {
          // push options to redux

          const registers = registersRes.data;

          dispatch({
            type: "facility.update",
            meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
            payload: {
              isLoaded: true,
              registers: registers,
            },
          });
        }
      } catch (e) {
        console.log("GetRegisters ERR:", e);
      }
    }
  };
}

export function loadStaffAccounts(token?: CancelToken) {
  return async (dispatch: any, getState: any) => {
    if (getState().facilityStore.staffAccounts.length === 0) {
      try {
        const usersRes = await GetFacilityAdmin({roles: true}, false);

        if (token && token.reason) {
          return;
        }

        console.log("staffAccountRes: ", usersRes);
        if (usersRes.status === StatusCode.OK) {
          // push options to redux

          const staffAccounts = usersRes.data;

          dispatch({
            type: "facility.update",
            meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
            payload: {
              isLoaded: true,
              staffAccounts: staffAccounts,
            },
          });
        }
      } catch (e) {
        console.log("GetStaffAccounts ERR:", e);
      }
    }
  };
}

export function loadDiscountOptions() {
  return async (dispatch: any, getState: any) => {
    if (getState().facilityStore.discountOptions.length === 0) {
      try {
        const discountRes = await GetDiscount();
        console.log("discounttRes: ", discountRes);
        if (discountRes.status === StatusCode.OK) {
          // push options to redux

          const discountOptions = discountRes.data;

          dispatch({
            type: "facility.update",
            meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
            payload: {
              isLoaded: true,
              discountOptions: discountOptions,
            },
          });
        }
      } catch (e) {
        console.log("GetDiscount ERR:", e);
      }
    }
  };
}

export function loadFacilityWeather(facility_id: number, useGlobalLoader: boolean) {
  return async (dispatch: any) => {
    const lang = getOpenWeatherLang(i18n.language);
    const res = await GetWeather(facility_id, lang, useGlobalLoader);

    if (res.status !== StatusCode.OK) {
      console.log(res);
      return;
    }

    // console.log("Facility Weather: ", res.data);

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        weather: res.data,
      },
    });
  };
}

export function updateFacilityTeeSheetSettings(params: IPutTeeSheetSettings, useGlobalLoader: boolean) {
  return async (dispatch: any) => {
    const res = await PutFacilityTeeSheetSettings(params, useGlobalLoader);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(res, "Error updating Tee Sheet Settings")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated")));

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        teeSheetSettings: {
          ...res.data,
        },
      },
    });
  };
}

export function loadFacilityTeeSheetSettings() {
  return async (dispatch: any) => {
    const settingsRes = await GetFacilityTeeSheetSettings(null, false);
    if (settingsRes.status !== StatusCode.OK) {
      console.log("ERROR", settingsRes);
      return;
    }

    console.log("Facility TeeSheetSettings: ", settingsRes);

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        teeSheetSettings: {
          ...settingsRes.data,
        },
      },
    });
  };
}

export function loadFacilityPrinterSettings() {
  return async (dispatch: any) => {
    const settingsRes = await GetFacilityPrinterSettings(false);
    if (settingsRes.status !== StatusCode.OK) {
      console.log("ERROR", settingsRes);
      return;
    }

    console.log("Facility TeeSheetSettings: ", settingsRes);

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        printerSettings: {
          ...(settingsRes.data as Record<string, any>),
        },
      },
    });
  };
}

export function updateFacilityPrinterSettings(params: IPutPrinterSettings, useGlobalLoader: boolean) {
  return async (dispatch: any) => {
    const res = await PutFacilityPrinterSettings(params, useGlobalLoader);
    if (res.status !== StatusCode.OK) {
      console.log(res.message);
      return;
    }

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        printerSettings: {
          ...(res.data as Record<string, any>),
        },
      },
    });
  };
}

export function loadFacilityKitchenCourses(params: { id: number }, useGlobalLoader?: boolean) {
  return async (dispatch: any) => {
    const res = await GetKitchenMeals(params, useGlobalLoader ?? true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    //Sort by course position
    const sortedCourses = res?.data?.sort((currentCourse: IKitchenCourse, nextCourse: IKitchenCourse) => {
      if (currentCourse?.position > nextCourse?.position) {
        return 1;
      } else if (currentCourse?.position < nextCourse?.position) {
        return -1;
      } else {
        return 0;
      }
    });

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        kitchenCourses: sortedCourses,
      },
    });
  };
}

export function loadAllFacilities(params: TGetFacility, useGlobalLoader: boolean) {
  return async (dispatch: any) => {
    const res = await GetFacility(params, useGlobalLoader);
    if (res.status !== StatusCode.OK) {
      console.log(res);
      return;
    }

    console.log("All Facilities: ", res.data[0]);

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        isLoaded: true,
        facilities: res.data,
      },
    });
  };
}

export function loadProductValidation(token?: CancelToken) {
  return async (dispatch: any, getState: any) => {
    if (getState().facilityStore.validationNewProduct.required === undefined) {
      const res = await GetProductRequirements(null, token ? false : true, token);
      if (token && token.reason) {
        return;
      }
      if (res.status !== StatusCode.OK) {
        return;
      }

      dispatch({
        type: "facility.update",
        meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
        payload: {
          isLoaded: true,
          validationNewProduct: {
            required: {
              ...(getState().facilityStore.validationNewProduct.required as TProductInputsRequired),
              ...(res.data as TProductRequirements),
            },
          },
        },
      });
    }
  };
}

export function setRegisterSheet(open: boolean) {
  return (dispatch: any) => {
    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        openRegistersSheet: open,
      },
    });
  };
}

export function logoutFacility() {
  return (dispatch: any) => {
    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        facility: null,
      },
    });
  };
}

export function loadLoyaltyProgram(program?: ILoyaltyProgram, cancelToken?: CancelToken) {
  return async (dispatch: any) => {
    //Update the loyalty program without calling api
    if (program) {
      dispatch({
        type: "facility.update",
        meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
        payload: {
          loyaltyProgram: program,
        },
      });
    } else {
      //Load the loyalty program
      return new Promise<ILoyaltyProgram>(resolve => {
        void GetLoyaltyProgram(false, cancelToken).then(res => {
          if (res.status !== StatusCode.OK) {
            resolve(undefined);
            dispatch({
              type: "facility.update",
              meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
              payload: {
                loyaltyProgram: null,
              },
            });
          } else {
            const program = res.data as ILoyaltyProgram;
            dispatch({
              type: "facility.update",
              meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
              payload: {
                loyaltyProgram: program ? {...program, enrolment_customer_types: program?.enrolment_customer_types === null ? [] : program?.enrolment_customer_types} : program,
              },
            });

            resolve(program);
          }
        });
      });
    }
  };
}

export function loadLoyaltyProgramOffers(offers?: Array<IOffer> | null, cancelToken?: CancelToken) {
  return async (dispatch: any) => {
    // Update the loyalty program offers without calling api
    if (offers !== undefined && offers !== null) {
      dispatch({
        type: "facility.update",
        meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
        payload: {
          loyaltyProgramOffers: offers,
        },
      });
      return offers;
    }

    // Load the loyalty program offers
    const res = await GetLoyaltyProgramOffers(false, cancelToken);
    if (res.status !== StatusCode.OK) {
      dispatch({
        type: "facility.update",
        meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
        payload: {
          loyaltyProgramOffers: null,
        },
      });
      return undefined;
    }

    const nextOffers = res.data;
    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        loyaltyProgramOffers: nextOffers,
      },
    });
    return nextOffers;
  };
}

export function loadServiceFees(params: IGetServiceFees, useGlobalLoader: boolean) {
  return async (dispatch: any) => {
    const res = await GetServiceFees(params, useGlobalLoader);

    if (res.status !== StatusCode.OK) {
      console.log(res);
      return;
    }

    console.log("Service Fees: ", res.data);

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        isLoaded: true,
        serviceFees: res.data,
      },
    });
  };
}

export interface IFacilityActions {
  loadAdminFacility: typeof loadAdminFacility;
  loadAllFacilities: typeof loadAllFacilities;
  loadFacilityWeather: typeof loadFacilityWeather;
  loadPaymentOptions: typeof loadPaymentOptions;
  loadDiscountOptions: typeof loadDiscountOptions;
  loadFacilityTeeSheetSettings: typeof loadFacilityTeeSheetSettings;
  updateFacilityTeeSheetSettings: typeof updateFacilityTeeSheetSettings;
  loadFacilityPrinterSettings: typeof loadFacilityPrinterSettings;
  updateFacilityPrinterSettings: typeof updateFacilityPrinterSettings;
  loadFacilityKitchenCourses: typeof loadFacilityKitchenCourses;
  loadProductValidation: typeof loadProductValidation;
  loadRegisters: typeof loadRegisters;
  loadStaffAccounts: typeof loadStaffAccounts;
  setRegisterSheet: typeof setRegisterSheet;
  logoutFacility: typeof logoutFacility;
  loadLoyaltyProgram: typeof loadLoyaltyProgram;
  loadLoyaltyProgramOffers: typeof loadLoyaltyProgramOffers;
  loadServiceFees: typeof loadServiceFees;
}
